import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { fetchModels } from '../api';
import { AnalyticsTab } from '../components/AnalyticsTab';
import { ApiDocsTab } from '../components/ApiDocsTab';
import { ProviderPill } from '../components/EndpointBadge';
import { PlaygroundTab, type PlaygroundPrefill } from '../components/PlaygroundTab';
import { RequestHistory } from '../components/RequestHistory';
import { SettingsTab } from '../components/SettingsTab';
import type { ApiCallRecord, ApiCallResponse, ApiEndpointModel } from '../types';

type TabId = 'playground' | 'requests' | 'analytics' | 'docs' | 'settings';

/** Map a recorded call to the response shape the Result panel renders. */
function recordToResult(r: ApiCallRecord): ApiCallResponse {
  return {
    record_id: r.id,
    status: r.status,
    model_type: r.model_type,
    model_id: r.model_id,
    provider: r.provider,
    output_image: null,
    output_url: r.output_ref,
    cost_usd: r.cost_usd,
    elapsed_ms: r.duration_seconds != null ? Math.round(r.duration_seconds * 1000) : null,
    error: r.error,
  };
}

const TABS: { id: TabId; label: string }[] = [
  { id: 'playground', label: 'Playground' },
  { id: 'requests', label: 'Requests' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'settings', label: 'Settings' },
  { id: 'docs', label: 'Docs' },
];

export function EndpointPage() {
  const { endpoint } = useParams<{ endpoint: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [models, setModels] = useState<ApiEndpointModel[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<PlaygroundPrefill | undefined>(undefined);

  const tabParam = searchParams.get('tab');
  const activeTab: TabId =
    tabParam === 'requests'
      ? 'requests'
      : tabParam === 'analytics'
        ? 'analytics'
        : tabParam === 'settings'
          ? 'settings'
          : tabParam === 'docs'
            ? 'docs'
            : 'playground';

  useEffect(() => {
    let cancelled = false;
    fetchModels()
      .then((m) => {
        if (!cancelled) setModels(m);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const model = useMemo(
    () => models?.find((m) => m.path === `/v1/${endpoint}`) || null,
    [models, endpoint],
  );

  function setTab(tab: TabId) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  }

  function openInPlayground(record: ApiCallRecord) {
    setPrefill({
      modelKey: model?.key ?? '',
      prompt: record.prompt,
      params: record.params,
      seed: record.seed,
      meta: {
        scan_id: record.scan_id,
        organization_id: record.organization_id,
        job_id: record.job_id,
        view: record.view,
        player: record.player,
        dvp: record.dvp,
      },
      inputRefs: record.input_refs,
      result: recordToResult(record),
    });
    setTab('playground');
  }

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm">
        Failed to load endpoint: {loadError}
      </div>
    );
  }

  if (!models) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }

  if (!model) {
    return (
      <div className="max-w-3xl mx-auto">
        <p className="text-sm text-slate-500">
          Unknown endpoint <span className="font-mono">{endpoint}</span>.{' '}
          <Link to="/gen-api" className="text-indigo-600 hover:underline">
            Back to endpoints
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-4">
        <Link to="/gen-api" className="text-xs text-indigo-600 hover:underline">
          ← All endpoints
        </Link>
        <div className="flex items-center gap-2 mt-1">
          <h1 className="text-2xl font-semibold text-slate-900">{model.label}</h1>
          <ProviderPill
            provider={model.active_provider || model.provider}
            configuredProvider={model.provider}
          />
        </div>
        <p className="text-sm text-slate-500 mt-1">{model.description}</p>
        <p className="text-xs text-slate-400 mt-1 font-mono">
          {model.model_id || model.endpoint} · POST {model.path}
        </p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-slate-200 mb-5">
        <nav className="flex gap-6" aria-label="Endpoint views">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              className={`relative -mb-px py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-indigo-600 border-b-2 border-indigo-500'
                  : 'text-slate-500 border-b-2 border-transparent hover:text-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'playground' && (
        <PlaygroundTab key={model.key} model={model} prefill={prefill} />
      )}
      {activeTab === 'requests' && (
        <RequestHistory model={model} onOpenInPlayground={openInPlayground} />
      )}
      {activeTab === 'analytics' && <AnalyticsTab model={model} />}
      {activeTab === 'settings' && <SettingsTab model={model} />}
      {activeTab === 'docs' && <ApiDocsTab model={model} />}
    </div>
  );
}
